import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import { format } from 'date-fns';
import { FiDownload, FiArrowRight } from 'react-icons/fi';
import './PublicQuotation.css';

const PublicQuotation = () => {
  const { confirm } = useConfirm();
  const { token } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const [changeMessage, setChangeMessage] = useState('');

  useEffect(() => {
    fetchQuotation();
  }, [token]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const response = await publicAPI.getQuotation(token);
      setQuotation(response.data.quotation);
    } catch (err) {
      setError(err.response?.data?.error || 'Quotation not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: quotation?.currency || 'AED',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      accepted: 'badge-accepted',
      declined: 'badge-declined',
      expired: 'badge-expired',
      converted: 'badge-converted',
    };
    return statusMap[status] || 'badge-draft';
  };

  const getDisplayStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await publicAPI.getQuotationPDF(token);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quotation.quotation_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to generate PDF');
    }
  };

  const handleAccept = async () => {
    const confirmed = await confirm({
      title: 'Accept Quotation',
      message: 'Are you sure you want to accept this quotation?',
      type: 'success',
      confirmText: 'Accept',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      setActionLoading(true);
      await publicAPI.acceptQuotation(token);
      alert('Quotation accepted successfully!');
      fetchQuotation();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    const confirmed = await confirm({
      title: 'Reject Quotation',
      message: 'Are you sure you want to reject this quotation?',
      type: 'danger',
      confirmText: 'Reject',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      setActionLoading(true);
      await publicAPI.rejectQuotation(token);
      alert('Quotation rejected.');
      fetchQuotation();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChange = async () => {
    if (!changeMessage.trim()) {
      alert('Please enter your change request details.');
      return;
    }
    
    try {
      setActionLoading(true);
      await publicAPI.requestQuotationChange(token, changeMessage);
      alert('Change request submitted successfully!');
      setShowChangeRequest(false);
      setChangeMessage('');
      fetchQuotation();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit change request');
    } finally {
      setActionLoading(false);
    }
  };

  const canTakeAction = ['sent', 'viewed'].includes(quotation?.status);

  if (loading) {
    return (
      <div className="public-loading">
        <div className="loading-spinner"></div>
        <p>Loading quotation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-error">
        <div className="error-icon">!</div>
        <h2>Quotation Not Found</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="public-quotation">
      <div className="public-header">
        <div className="public-header-content">
          <div className="public-logo">
            <FiArrowRight className="logo-icon" />
            <span>iOnline ERP</span>
          </div>
          <div className="public-actions">
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FiDownload /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="public-content">
        <div className="quotation-document">
          {/* Quotation Header - Top Row: Logo + Company Name | Quotation Details */}
          <div className="quotation-header-top">
            <div className="quotation-logo-section">
              <img 
                src={quotation.company_logo || '/logo.svg'} 
                alt="Company Logo" 
                className="quotation-logo" 
              />
              <div className="quotation-company-name">{quotation.company_name || 'Your Company'}</div>
              {quotation.company_name_arabic && <div className="quotation-company-name-arabic">{quotation.company_name_arabic}</div>}
            </div>
            <div className="quotation-details-section">
              <h2 className="quotation-number">{quotation.quotation_number}</h2>
              <div className="detail-row">
                <span className="detail-label">Issue Date:</span>
                <span className="detail-value">{formatDate(quotation.issue_date)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Valid Until:</span>
                <span className="detail-value">{formatDate(quotation.expiry_date)}</span>
              </div>
              {quotation.po_number && (
                <div className="detail-row">
                  <span className="detail-label">PO Number:</span>
                  <span className="detail-value">{quotation.po_number}</span>
                </div>
              )}
              <div className="detail-row status-row">
                <span className="detail-label">Status:</span>
                <span className={`status-badge status-${quotation.status}`}>
                  {getDisplayStatus(quotation.status)}
                </span>
              </div>
            </div>
          </div>

          {/* Divider Line */}
          <div className="header-divider"></div>

          {/* FROM & BILL TO Section - Side by Side */}
          <div className="quotation-parties">
            <div className="party-section">
              <h4 className="party-label">FROM</h4>
              <p className="party-name">{quotation.company_name || 'Your Company'}</p>
              {quotation.company_address && <p className="party-detail">{quotation.company_address}</p>}
              {(quotation.company_city || quotation.company_country) && (
                <p className="party-detail">{[quotation.company_city, quotation.company_country].filter(Boolean).join(', ')}</p>
              )}
              {quotation.company_email && <p className="party-detail">{quotation.company_email}</p>}
              {quotation.company_phone && <p className="party-detail">{quotation.company_phone}</p>}
            </div>
            <div className="party-section">
              <h4 className="party-label">QUOTE TO</h4>
              <p className="party-name">{quotation.client_company}</p>
              {quotation.client_contact && <p className="party-detail">{quotation.client_contact}</p>}
              {quotation.client_email && <p className="party-detail">{quotation.client_email}</p>}
              {quotation.client_address && <p className="party-detail">{quotation.client_address}</p>}
            </div>
          </div>

          {/* Items Table */}
          <div className="quotation-items">
            <table>
              <thead>
                <tr>
                  {quotation.display_fields?.sku && <th>SKU</th>}
                  {quotation.display_fields?.category && <th>Category</th>}
                  {quotation.display_fields?.item_name !== false && <th>Item Name</th>}
                  {quotation.display_fields?.description !== false && <th style={{ width: '40%' }}>Description</th>}
                  {quotation.display_fields?.unit_price && <th style={{ textAlign: 'right' }}>Unit Price</th>}
                  {quotation.display_fields?.unit && <th style={{ textAlign: 'center' }}>Unit</th>}
                  {quotation.display_fields?.quantity && <th style={{ textAlign: 'center' }}>Qty</th>}
                  {quotation.display_fields?.tax && <th style={{ textAlign: 'right' }}>Tax</th>}
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {quotation.items.map((item, index) => (
                  <tr key={index}>
                    {quotation.display_fields?.sku && <td>{item.sku || '-'}</td>}
                    {quotation.display_fields?.category && <td>{item.category || '-'}</td>}
                    {quotation.display_fields?.item_name !== false && <td className="item-name">{item.name}</td>}
                    {quotation.display_fields?.description !== false && <td>{item.description || '-'}</td>}
                    {quotation.display_fields?.unit_price && <td style={{ textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>}
                    {quotation.display_fields?.unit && <td style={{ textAlign: 'center' }}>{item.unit || '-'}</td>}
                    {quotation.display_fields?.quantity && <td style={{ textAlign: 'center' }}>{item.quantity}</td>}
                    {quotation.display_fields?.tax && <td style={{ textAlign: 'right' }}>{item.tax_rate || 0}%</td>}
                    {/* Row amount excludes VAT so rows reconcile with the pre-tax Subtotal */}
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(Number(item.total || 0) - Number(item.tax_amount || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="quotation-totals">
            <div className="total-row">
              <span>Subtotal</span>
              <span>{formatCurrency(quotation.subtotal)}</span>
            </div>
            {quotation.discount_amount > 0 && (
              <div className="total-row discount">
                <span>Discount</span>
                <span>-{formatCurrency(quotation.discount_amount)}</span>
              </div>
            )}
            <div className="total-row">
              <span>Tax</span>
              <span>{formatCurrency(quotation.tax_amount)}</span>
            </div>
            <div className="total-row grand-total">
              <span>Total</span>
              <span>{formatCurrency(quotation.total)}</span>
            </div>
          </div>

          {/* Payment Terms (Installments) */}
          {quotation.payment_terms && quotation.payment_terms.type && quotation.payment_terms.type !== 'full' && (
            <div className="payment-terms-section">
              <h4>Payment Terms</h4>
              <div className="installments-list">
                {quotation.payment_terms.installments && quotation.payment_terms.installments.map((installment, index) => (
                  <div key={index} className="installment-row">
                    <span className="installment-label">Payment {index + 1} ({installment.percentage}%)</span>
                    <span className="installment-amount">{formatCurrency(quotation.total * (installment.percentage / 100))}</span>
                    <span className="installment-due">Due: {installment.due_days ? `${installment.due_days} days` : 'Upon acceptance'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes & Terms */}
          <div className="quotation-footer">
            {quotation.notes && (
              <div className="quotation-notes">
                <h4>Notes</h4>
                <p>{quotation.notes}</p>
              </div>
            )}
            {quotation.terms && (
              <div className="quotation-terms">
                <h4>Terms & Conditions</h4>
                <p>{quotation.terms}</p>
              </div>
            )}
          </div>

          {/* Action Buttons for Clients */}
          {canTakeAction && (
            <div className="quotation-actions">
              <h4>Quotation Actions</h4>
              <div className="action-buttons">
                <button 
                  className="btn btn-success" 
                  onClick={handleAccept}
                  disabled={actionLoading}
                >
                  Accept Quotation
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={() => setShowChangeRequest(!showChangeRequest)}
                  disabled={actionLoading}
                >
                  Request Changes
                </button>
                <button 
                  className="btn btn-danger" 
                  onClick={handleReject}
                  disabled={actionLoading}
                >
                  Reject
                </button>
              </div>
              
              {showChangeRequest && (
                <div className="change-request-form">
                  <h5>Describe the changes you need:</h5>
                  <textarea
                    value={changeMessage}
                    onChange={(e) => setChangeMessage(e.target.value)}
                    placeholder="Please describe what changes you would like to request..."
                    rows={4}
                  />
                  <div className="form-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={handleRequestChange}
                      disabled={actionLoading || !changeMessage.trim()}
                    >
                      Submit Request
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => { setShowChangeRequest(false); setChangeMessage(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show change request info if status is change_requested */}
          {quotation.status === 'change_requested' && quotation.change_request && (
            <div className="change-request-info">
              <h4>Change Requested</h4>
              <p className="change-request-message">{quotation.change_request}</p>
              <p className="change-request-date">
                Requested on: {formatDate(quotation.change_request_at)}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="public-footer">
        <p>Powered by iOnline ERP - Professional Invoicing Solution</p>
      </div>
    </div>
  );
};

export default PublicQuotation;